import { BlockerFunction, useBlocker } from "react-router-dom";
import { useCallback, useEffect, useRef } from "react";

interface UnsavedChangesBlocker {
  allowNextNavigation: () => void;
  isBlocked: boolean;
  proceed: () => void;
  reset: () => void;
}

/**
 * @param when true if there are unsaved changes pending
 * @returns an object containing:
 *   - `isBlocked`, which indicates whether a navigation is currently being blocked;
 *   - `allowNextNavigation`, a function which can be called before a programmatic
 *     navigation to allow it to bypass the blocker;
 *   - `proceed`, a function which resolves a blocked navigation by allowing it
 *     to continue;
 *   - `reset`, a function which resolves a blocked navigation by canceling it.
 */
export const useUnsavedChangesBlocker = (
  when: boolean
): UnsavedChangesBlocker => {
  const allowNextNavigationRef = useRef(false);
  const proceedingRef = useRef(false);
  const shouldBlock = useCallback<BlockerFunction>(
    ({ currentLocation, nextLocation }) =>
      when &&
      !allowNextNavigationRef.current &&
      currentLocation.pathname !== nextLocation.pathname,
    [when]
  );
  const blocker = useBlocker(shouldBlock);

  useEffect(() => {
    if (!when) {
      return;
    }
    const handleBeforeUnload = (event: BeforeUnloadEvent): void => {
      if (allowNextNavigationRef.current) {
        return;
      }
      event.preventDefault();
      // Deprecated, but still required for some browsers:
      // https://developer.mozilla.org/en-US/docs/Web/API/BeforeUnloadEvent/returnValue
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [when]);

  const allowNextNavigation = useCallback(() => {
    allowNextNavigationRef.current = true;
    queueMicrotask(() => {
      allowNextNavigationRef.current = false;
    });
  }, []);

  const proceed = useCallback(() => {
    if (blocker.state !== "blocked") {
      return;
    }
    proceedingRef.current = true;
    blocker.proceed();
  }, [blocker]);

  const reset = useCallback(() => {
    if (proceedingRef.current) {
      proceedingRef.current = false;
      return;
    }
    if (blocker.state === "blocked") {
      blocker.reset();
    }
  }, [blocker]);

  return {
    allowNextNavigation,
    isBlocked: blocker.state === "blocked",
    proceed,
    reset,
  };
};
